import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getQuestion, submitSession, getSessionStatus, getSessionSections } from '../../api/candidate.api';
import useAntiCheat from '../../hooks/useAntiCheat';
import useFullscreen from '../../hooks/useFullscreen';
import useServerTimer from '../../hooks/useServerTimer';
import useAutosave from '../../hooks/useAutosave';
import TimerBar from '../../components/candidate/TimerBar';
import NavigationPanel from '../../components/candidate/NavigationPanel';
import WarningModal from '../../components/candidate/WarningModal';
import SubmitConfirmModal from '../../components/candidate/SubmitConfirmModal';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';

const ExamTaking = () => {
  const { id: examId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = Number(searchParams.get('session'));
  const navigate = useNavigate();

  const [deadlineAt, setDeadlineAt] = useState(null);
  const [serverNow, setServerNow] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [maxTabSwitches, setMaxTabSwitches] = useState(3);
  const [initialized, setInitialized] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [question, setQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [isMarkedReview, setIsMarkedReview] = useState(false);
  const [qLoading, setQLoading] = useState(false);

  const [sectionStructure, setSectionStructure] = useState([]);

  const [answeredSet, setAnsweredSet] = useState(new Set());
  const [markedSet, setMarkedSet] = useState(new Set());
  const [answeredCount, setAnsweredCount] = useState(0);

  const [warningOpen, setWarningOpen] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submittedRef = useRef(false);

  const { isFullscreen, enter: enterFullscreen } = useFullscreen();
  const { save: autosave, flush: autosaveFlush } = useAutosave(sessionId);

  // Load session status
  useEffect(() => {
    if (!sessionId) { navigate('/candidate/exams'); return; }
    getSessionStatus(sessionId)
      .then((res) => {
        const data = res.data.data;
        if (data.status !== 'in_progress' && data.status !== 'not_started') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/';
          return;
        }
        setDeadlineAt(data.deadlineAt);
        setServerNow(data.serverNow);
        setAnsweredCount(data.answeredCount || 0);
        setInitialized(true);
      })
      .catch(() => setError('Failed to load session. Please refresh.'));
  }, [sessionId, examId, navigate]);

  // Periodic timer sync (30s)
  useEffect(() => {
    if (!initialized) return;
    const interval = setInterval(() => {
      getSessionStatus(sessionId)
        .then((res) => {
          const data = res.data.data;
          setServerNow(data.serverNow);
          setAnsweredCount(data.answeredCount || 0);
          if (data.status !== 'in_progress') {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
          }
        })
        .catch(() => {});
    }, 30_000);
    return () => clearInterval(interval);
  }, [initialized, sessionId, examId, navigate]);

  // Load section structure once after session is initialized
  useEffect(() => {
    if (!initialized) return;
    getSessionSections(sessionId)
      .then((res) => setSectionStructure(res.data.sections || []))
      .catch(() => {});
  }, [initialized, sessionId]);

  // Load question
  useEffect(() => {
    if (!initialized) return;
    setQLoading(true);
    getQuestion(sessionId, currentIndex)
      .then((res) => {
        const { question: q, savedAnswer, totalQuestions: total } = res.data;
        setQuestion(q);
        setTotalQuestions(total);
        setSelectedOption(savedAnswer?.selected_option_id ?? null);
        setTextAnswer(savedAnswer?.text_answer ?? '');
        setIsMarkedReview(savedAnswer?.is_marked_review ?? false);
        const wasAnswered =
          savedAnswer?.selected_option_id != null ||
          (savedAnswer?.text_answer ?? '').length > 0;
        if (wasAnswered) {
          setAnsweredSet((prev) => (prev.has(currentIndex) ? prev : new Set(prev).add(currentIndex)));
        }
        if (savedAnswer?.is_marked_review) {
          setMarkedSet((prev) => (prev.has(currentIndex) ? prev : new Set(prev).add(currentIndex)));
        }
        if (!maxTabSwitches && res.data.maxTabSwitches) setMaxTabSwitches(res.data.maxTabSwitches);
      })
      .catch(() => setError('Failed to load question. Please try again.'))
      .finally(() => setQLoading(false));
  }, [currentIndex, initialized, sessionId, maxTabSwitches]);

  const performSubmit = useCallback(async (reason = 'manual') => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      if (question && (selectedOption !== null || textAnswer)) {
        await autosaveFlush(question.id, selectedOption, isMarkedReview, textAnswer).catch(() => {});
      }
      await submitSession(sessionId, { submit_reason: reason });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    } catch (err) {
      if (err.response?.status === 409) {
        // Already submitted (e.g. timer expired) — redirect cleanly
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      } else {
        setError('Submission failed. Please try again.');
        setSubmitting(false);
        submittedRef.current = false;
      }
    }
  }, [question, selectedOption, textAnswer, isMarkedReview, autosaveFlush, sessionId]);

  const handleTabSwitch = useCallback((count) => {
    setTabSwitchCount(count);
    setWarningOpen(true);
  }, []);

  const handleAutoSubmit = useCallback(async (reason) => {
    if (submittedRef.current) return;
    setWarningOpen(false);
    await performSubmit(reason);
  }, [performSubmit]);

  useAntiCheat({
    sessionId,
    maxTabSwitches,
    onTabSwitch: handleTabSwitch,
    onAutoSubmit: handleAutoSubmit,
    active: initialized && !submittedRef.current,
  });

  const handleTimerExpired = useCallback(async () => {
    if (submittedRef.current) return;
    await performSubmit('timeout');
  }, [performSubmit]);

  const timer = useServerTimer(deadlineAt, serverNow, handleTimerExpired);

  const handleSelectOption = (optionId) => {
    setSelectedOption(optionId);
    if (!answeredSet.has(currentIndex)) {
      setAnsweredCount((c) => c + 1);
    }
    const newAnswered = new Set(answeredSet);
    newAnswered.add(currentIndex);
    setAnsweredSet(newAnswered);
    autosave(question.id, optionId, isMarkedReview, null);
  };

  const handleTextAnswerChange = (val) => {
    setTextAnswer(val);
    const had = answeredSet.has(currentIndex);
    const newAnswered = new Set(answeredSet);
    if (val) {
      newAnswered.add(currentIndex);
      if (!had) setAnsweredCount((c) => c + 1);
    } else {
      newAnswered.delete(currentIndex);
      if (had) setAnsweredCount((c) => Math.max(0, c - 1));
    }
    setAnsweredSet(newAnswered);
    autosave(question.id, null, isMarkedReview, val);
  };

  const navigateTo = async (index) => {
    if (question && (selectedOption !== null || textAnswer)) {
      await autosaveFlush(question.id, selectedOption, isMarkedReview, textAnswer).catch(() => {});
    }
    setCurrentIndex(index);
  };

  const handleOpenSubmitModal = async () => {
    if (question && (selectedOption !== null || textAnswer)) {
      await autosaveFlush(question.id, selectedOption, isMarkedReview, textAnswer).catch(() => {});
    }
    try {
      const res = await getSessionStatus(sessionId);
      setAnsweredCount(res.data.data.answeredCount || 0);
    } catch {}
    setSubmitModalOpen(true);
  };

  const handleMarkReview = () => {
    const newMark = !isMarkedReview;
    setIsMarkedReview(newMark);
    const newMarked = new Set(markedSet);
    if (newMark) newMarked.add(currentIndex);
    else newMarked.delete(currentIndex);
    setMarkedSet(newMarked);
    if (question) autosave(question.id, selectedOption, newMark, textAnswer);
  };

  if (!initialized) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="text-slate-400 text-sm mt-3">Loading exam…</p>
      </div>
    </div>
  );

  const optionLetters = 'ABCDE';

  return (
    <div className="relative min-h-screen bg-slate-900 text-white no-select flex flex-col">
      {/* Static ambient depth glow (no motion — preserves focus) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 12% 18%, rgba(37,99,235,0.08), transparent 45%), radial-gradient(circle at 88% 82%, rgba(99,102,241,0.07), transparent 45%)',
        }}
      />

      {/* Top bar */}
      <header className="relative bg-slate-850 border-b border-slate-700/50 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Brand */}
          <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className="font-semibold text-slate-100 text-sm">Assessment Portal</span>

          {/* Fullscreen warning pill */}
          {!isFullscreen && (
            <button
              onClick={() => enterFullscreen().catch(() => {})}
              className="flex items-center gap-1.5 text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-full font-medium hover:bg-amber-500/30 transition-colors animate-pulse-slow"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Enter Fullscreen
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <TimerBar {...timer} />
          <Button
            variant="danger"
            size="sm"
            onClick={handleOpenSubmitModal}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Submit
          </Button>
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className="bg-red-900/50 border-b border-red-800 text-red-200 px-6 py-2 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {/* Main content */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Question area */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {qLoading ? (
            <div className="flex items-center justify-center py-24">
              <Spinner size="lg" />
            </div>
          ) : question ? (
            <div className="max-w-2xl mx-auto">
              {/* Section header */}
              {question.section && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-slate-700/60" />
                  <span className="text-xs font-semibold text-primary-400 uppercase tracking-widest px-2">
                    {question.section}
                  </span>
                  <div className="h-px flex-1 bg-slate-700/60" />
                </div>
              )}
              {/* Question meta */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-primary-600/20 text-primary-400 rounded-xl text-sm font-bold flex items-center justify-center">
                    {currentIndex + 1}
                  </span>
                  <div>
                    <p className="text-slate-300 text-sm font-medium">
                      Question {currentIndex + 1} <span className="text-slate-500">of {totalQuestions}</span>
                    </p>
                    <p className="text-slate-500 text-xs">{question.marks} mark{question.marks !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button
                  onClick={handleMarkReview}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl border transition-all duration-150 font-medium ${
                    isMarkedReview
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill={isMarkedReview ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {isMarkedReview ? 'Marked' : 'Mark for Review'}
                </button>
              </div>

              {/* Question text */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6 mb-5">
                <p className="text-base leading-relaxed text-slate-100">{question.question_text}</p>
              </div>

              {/* Answer input — varies by question type */}
              <div className="mb-8">
                {(question.question_type === 'mcq' || question.question_type === 'true_false' || !question.question_type) && (
                  <div className="space-y-2.5">
                    {question.options?.filter(Boolean).map((opt, i) => (
                      <label
                        key={opt.id}
                        className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all duration-150 ${
                          selectedOption === opt.id
                            ? 'border-primary-500 bg-primary-500/15 shadow-[inset_0_0_20px_rgba(59,130,246,0.18),0_0_24px_rgba(59,130,246,0.18)] ring-1 ring-primary-400/30'
                            : 'border-slate-700 bg-slate-800/60 hover:border-slate-600 hover:bg-slate-800'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          selectedOption === opt.id ? 'border-primary-500 bg-primary-500' : 'border-slate-600'
                        }`}>
                          {selectedOption === opt.id && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                        <input type="radio" name="option" value={opt.id} checked={selectedOption === opt.id}
                          onChange={() => handleSelectOption(opt.id)} className="sr-only" />
                        <span className="flex-1 text-sm text-slate-200 leading-relaxed">
                          <span className="font-semibold text-slate-400 mr-2">{optionLetters[i]}.</span>
                          {opt.option_text}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {question.question_type === 'fill_in_the_blank' && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Type your answer below:</p>
                    <input
                      type="text"
                      value={textAnswer}
                      onChange={(e) => handleTextAnswerChange(e.target.value)}
                      placeholder="Your answer…"
                      className="w-full bg-slate-800 border-2 border-slate-700 rounded-2xl px-4 py-3 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                )}

                {question.question_type === 'short_answer' && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Write your answer below (manually graded):</p>
                    <textarea
                      rows={5}
                      value={textAnswer}
                      onChange={(e) => handleTextAnswerChange(e.target.value)}
                      placeholder="Your answer…"
                      className="w-full bg-slate-800 border-2 border-slate-700 rounded-2xl px-4 py-3 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">This answer will be graded manually by the instructor.</p>
                  </div>
                )}
              </div>

              {/* Nav buttons */}
              <div className="flex items-center justify-between">
                <Button
                  variant="secondary"
                  onClick={() => navigateTo(currentIndex - 1)}
                  disabled={currentIndex === 0}
                  className="!bg-slate-800 !text-slate-300 !border-slate-700 hover:!bg-slate-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </Button>

                <span className="text-slate-500 text-xs">
                  {answeredCount} / {totalQuestions} answered
                </span>

                {currentIndex < totalQuestions - 1 ? (
                  <Button onClick={() => navigateTo(currentIndex + 1)}>
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                ) : (
                  <Button variant="success" onClick={handleOpenSubmitModal}>
                    Finish &amp; Submit
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </main>

        {/* Right sidebar */}
        <aside className="w-60 bg-slate-850 border-l border-slate-700/50 p-4 overflow-y-auto flex-shrink-0 hidden md:block">
          <NavigationPanel
            total={totalQuestions}
            current={currentIndex}
            answeredSet={answeredSet}
            markedSet={markedSet}
            onNavigate={navigateTo}
            sectionStructure={sectionStructure}
          />
        </aside>
      </div>

      <WarningModal
        isOpen={warningOpen}
        tabSwitchCount={tabSwitchCount}
        maxTabSwitches={maxTabSwitches}
        onClose={() => setWarningOpen(false)}
        onEnterFullscreen={() => { enterFullscreen().catch(() => {}); setWarningOpen(false); }}
      />

      <SubmitConfirmModal
        isOpen={submitModalOpen}
        onConfirm={() => { setSubmitModalOpen(false); performSubmit('manual'); }}
        onCancel={() => setSubmitModalOpen(false)}
        answeredCount={answeredCount}
        totalQuestions={totalQuestions}
        loading={submitting}
      />
    </div>
  );
};

export default ExamTaking;
